
import { useStore } from '@/store'
import { Options, Vue } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import * as otpService from '@/services/line-web-loyalty-bff/v1/otp'
import { alertMessageOptionsOtherCaseError } from '@/model/alert-options.model'
import {
  modalOptionsExceededRateLimit,
  modalOptionsExceededRateLimitWithBackReEnterNumber,
  modalOptionsOtpExceededRateLimit,
  modalOptionsOtpFailed,
  modalOptionsOtpTimeout,
  modalOptionsTooManyRequest,
  modalOptionsUpdateSucceed,
  modalTokenExpired,
} from '@/model/modal-options.model'
import { StepEditMobile } from '@/model/step-edit-mobile.model'
import * as meService from '@/services/line-web-loyalty-bff/v1/me'

@Options({
  computed: {
    newMobileNumber() {
      return this.$store.state.modalEditMobile.newMobileNumber
    },
    level() {
      return this.$store.state.modalEditMobile.level
    },
  },
})
export default class Otp extends Vue {
  @Prop() mobileNumber!: string
  @Prop() getCustomerProfileFn!: (token: string) => void

  private store = useStore()
  private otp = ''
  private refCodeNew = ''
  private timer = 60
  private isResendReady = false
  private isInputFocus = false
  private l2Token = ''

  mounted() {
    this.refCodeNew = this.store.state.modalEditMobile.refCode

    this.handleFocusInput()
    this.startCooldown()
  }

  getDisplayTime() {
    return new Date(this.timer * 1000).toISOString().substring(14, 19)
  }

  startCooldown(): void {
    setTimeout(() => {
      this.timer = this.timer - 1

      if (this.timer === 0) {
        this.isResendReady = true
      } else {
        this.startCooldown()
      }
    }, 1000)
  }

  handleUpdateOtp(event: Event) {
    const otpInputEl = event.target as HTMLInputElement

    if (otpInputEl.value.length > 6) {
      otpInputEl.value = otpInputEl.value.slice(0, 6)

      return
    }

    const otpValue = otpInputEl.value ?? ''
    const filteredValue = otpValue.replace(/[^0-9]+/g, '')

    this.otp = filteredValue

    if (this.otp.length === 6) {
      this.verifyOtp()
    }
  }

  handleFocusInput() {
    const otpInputEl = this.$refs.otpRef as HTMLInputElement

    if (otpInputEl) {
      this.isInputFocus = true

      otpInputEl.focus()
    }
  }

  handleUnfocusInput() {
    const otpInputEl = this.$refs.otpRef as HTMLInputElement

    if (otpInputEl) {
      this.isInputFocus = false

      otpInputEl.blur()
    }
  }

  handleBlur() {
    this.isInputFocus = false
  }

  handleKeydown(event: KeyboardEvent) {
    if (
      (event.keyCode &&
        event.keyCode !== 8 &&
        ((event.keyCode <= 48 && event.keyCode >= 57) ||
          (event.keyCode <= 96 && event.keyCode >= 105))) ||
      (event.key && event.key !== 'Backspace' && isNaN(parseInt(event.key)))
    ) {
      event.preventDefault()
    }
  }

  getOtpList() {
    const otpList = this.otp.toString().split('')

    if (otpList.length < 6) {
      for (let count = otpList.length; count < 6; count++) {
        otpList.push('')
      }
    }

    return otpList
  }

  resetOtp() {
    this.otp = ''
    this.handleFocusInput()
  }

  async verifyOtp() {
    this.handleUnfocusInput()
    this.store.commit('modalEditProfile/updateIsLoadingSpinner', true)

    const token = this.store.state.lineInfo.idToken
    if (token) {
      const formatMobile =
        '66' +
        (this.store.state.modalEditMobile.level === 'L1'
          ? this.mobileNumber.substring(1)
          : this.store.state.modalEditMobile.newMobileNumber.substring(1))

      try {
        const result = await otpService.verifyOtp(
          token,
          {
            mobile_no: formatMobile,
            ref_code: this.refCodeNew,
            otp: this.otp,
            l1_token: this.store.state.modalEditMobile.l1Token,
          },
          this.store.state.modalEditMobile.level
        )

        // handle unexptected error
        const statusCode = result?.json?.code

        if (statusCode === 200) {
          const {
            verification_result: verificationResult,
            l1_token: l1Token,
            l2_token: l2Token,
            customer_profiles: customerProfile,
          } = result?.json?.data

          if (this.store.state.modalEditMobile.level === 'L1') {
            this.store.commit('modalEditMobile/updateL1Token', l1Token ?? '')
          } else {
            this.store.commit('modalEditMobile/updateL2Token', l2Token ?? '')
            this.l2Token = l2Token ?? ''
          }

          this.store.commit('modalEditMobile/updateCustomerList', customerProfile ?? [])

          if (verificationResult && this.store.state.modalEditMobile.level === 'L1') {
            this.store.commit('modalEditMobile/updateStep', StepEditMobile.INPUT_NEW_MOBILE)
          } else if (customerProfile && this.store.state.modalEditMobile.level === 'L2') {
            if (!customerProfile.length) {
              await this.updatePatchMobile()
            } else {
              this.store.commit('modalEditMobile/updateStep', StepEditMobile.SHOW_CUSTOMER_LIST)
            }
          } else {
            this.handleUnexpectedError()
          }
        } else if (statusCode === 401) {
          const { error_detail: errorDetail } = result?.json?.error
          const errorReasons = errorDetail[0].reasons
          const isOtpExpired = errorReasons === 'OTP is expired'
          const isOtpInvalid = errorReasons === 'OTP is incorrect'
          const isTooManyAttempt = errorReasons === 'attempt count is over limits'

          if (isOtpInvalid || isOtpExpired || isTooManyAttempt) {
            this.store.dispatch(
              'modalNotification/onOpen',
              isOtpInvalid
                ? modalOptionsOtpFailed(this.store, this.resetOtp)
                : isOtpExpired
                ? modalOptionsOtpTimeout(this.store, this.resetOtp)
                : isTooManyAttempt
                ? this.store.state.modalEditMobile.level === 'L1' || this.store.state.modalEditMobile.level === 'L2'
                  ? modalOptionsOtpExceededRateLimit(this.store)
                  : modalOptionsExceededRateLimitWithBackReEnterNumber(this.store, () => {
                      this.store.commit(
                        'modalEditMobile/updateStep',
                        StepEditMobile.INPUT_NEW_MOBILE
                      )
                    })
                : this.handleUnexpectedError()
            )
          } else {
            this.handleUnexpectedError()
          }
        } else {
          this.handleUnexpectedError()
        }
      } catch (error) {
        this.store.commit('modalEditProfile/updateIsLoadingSpinner', false)
        this.handleUnexpectedError()
        throw new Error('พบข้อผิดพลาดบางประการ กรุณาลองใหม่อีกครั้ง')
      }
    }

    this.store.commit('modalEditProfile/updateIsLoadingSpinner', false)
  }

  async handleSendOtp() {
    if (this.isResendReady) {
      this.isResendReady = false
      this.timer = 60
      this.startCooldown()
      this.handleFocusInput()

      const token = this.store.state.lineInfo.idToken
      if (token) {
        const formatMobile =
          '66' +
          (this.store.state.modalEditMobile.level === 'L1'
            ? this.mobileNumber.substring(1)
            : this.store.state.modalEditMobile.newMobileNumber.substring(1))
        try {
          const result = await otpService.sendOtp(
            token,
            {
              mobile_no: formatMobile,
              l1_token: this.store.state.modalEditMobile.l1Token,
            },
            this.store.state.modalEditMobile.level
          )
          const statusCode = result?.json?.code

          if (statusCode === 200) {
            const { ref_code: refCode } = result?.json?.data

            if (refCode) {
              this.refCodeNew = refCode
            }
          } else if (statusCode === 401) {
            const { error_name: errorName } = result?.json?.error
            if (errorName === 'token_expired') {
              this.store.dispatch(
                'modalNotification/onOpenTokenExpired',
                modalTokenExpired(() => {
                  this.store.dispatch('modalEditMobile/onResetStep')
                  this.store.dispatch('modalEditMobile/onOpen')
                  this.store.dispatch('modalNotification/onCloseTokenExpired')
                })
              )
            } else {
              this.handleUnexpectedError()
            }
          } else if (statusCode === 429) {
            this.store.dispatch('modalEditMobile/onOpen')
            this.store.dispatch('modalNotification/onOpen', modalOptionsTooManyRequest(this.store))
          } else {
            this.handleUnexpectedError()
          }
        } catch (error) {
          this.store.dispatch('alertMessage/onOpen', alertMessageOptionsOtherCaseError)
          setTimeout(() => this.store.dispatch('alertMessage/onClose'), 3000)
        }
      } else {
        this.store.dispatch('alertMessage/onOpen', alertMessageOptionsOtherCaseError)
        setTimeout(() => this.store.dispatch('alertMessage/onClose'), 3000)
      }
    }
  }

  handleUnexpectedError() {
    this.store.dispatch('alertMessage/onOpen', alertMessageOptionsOtherCaseError)
    setTimeout(() => this.store.dispatch('alertMessage/onClose'), 3000)
    this.resetOtp()
  }

  async updatePatchMobile() {
    this.handleUnfocusInput()
    this.store.commit('modalEditProfile/updateIsLoadingSpinner', true)

    const token = this.store.state.lineInfo.idToken

    if (token) {
      try {
        let requestBody: any = {}
        if (this.store.state.modalEditMobile.customerProfileDopa.length > 0) {
          requestBody = this.store.state.modalEditMobile.customerProfileDopa[0]
        }
        requestBody.l2_token = this.l2Token

        const result = await meService.updateMobileNumber(token, requestBody)

        const statusCode = result?.json?.code

        if (statusCode === 200) {
          this.store.commit('modalEditProfile/updateIsLoadingSpinner', false)
          this.getCustomerProfileFn(token)
          this.store.commit('modalEditMobile/updateModalClose', true)
          this.store.commit('modalEditMobile/updateModalActive', false)
          this.store.dispatch(
            'modalNotification/onOpen',
            modalOptionsUpdateSucceed(this.store)
          )
        } else if (statusCode === 401) {
          const { error_name: errorName } = result?.json?.error
          if (errorName === 'token_expired') {
            this.store.dispatch(
              'modalNotification/onOpenTokenExpired',
              modalTokenExpired(() => {
                this.store.commit('modalEditMobile/updateStep', StepEditMobile.INPUT_NEW_MOBILE)
              })
            )
          } else {
            this.handleUnexpectedError()
          }
        } else if (statusCode === 429) {
          const { error_name: errorName } = result?.json?.error
          if (errorName === 'exceeded_rate_limit') {
            this.store.dispatch(
              'modalNotification/onOpen',
              modalOptionsExceededRateLimit(this.store)
            )
            this.store.dispatch('modalEditMobile/onClose')
          } else {
            this.handleUnexpectedError()
          }
        } else {
          this.handleUnexpectedError()
        }
      } catch (error) {
        this.store.commit('modalEditProfile/updateIsLoadingSpinner', false)
        this.handleUnexpectedError()
        throw new Error('พบข้อผิดพลาดบางประการ กรุณาลองใหม่อีกครั้ง')
      }
    }

    this.store.commit('modalEditProfile/updateIsLoadingSpinner', false)
  }
}
